import { registerEntityEndpoint, getClient } from "@editor/ui-utils";
import { getDefaultSeoDescription, updateDefaultValues } from "@modules/news-article-utils";
import { defineStore } from "pinia";
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  if (!getRandomValues) {
    getRandomValues = typeof crypto !== "undefined" && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);
    if (!getRandomValues) {
      throw new Error("crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported");
    }
  }
  return getRandomValues(rnds8);
}
const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 256).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + "-" + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + "-" + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + "-" + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + "-" + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}
const randomUUID = typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = {
  randomUUID
};
function v4(options, buf, offset) {
  if (native.randomUUID && true && !options) {
    return native.randomUUID();
  }
  options = options || {};
  const rnds = options.random || (options.rng || rng)();
  rnds[6] = rnds[6] & 15 | 64;
  rnds[8] = rnds[8] & 63 | 128;
  return unsafeStringify(rnds);
}
const ContentModelName = "Contents";
const ContentService = registerEntityEndpoint(ContentModelName);
const { listEntities, getEntity, postEntity, putEntity, deleteEntity } = ContentService;
const getContents = async (search = null) => {
  return listEntities({ params: { search: search || null } });
};
const getContentsFromIds = async (ids) => {
  return listEntities({
    params: {
      ...ids.length ? { uuid: ids } : {}
    }
  });
};
const getContent = async (id) => {
  return getEntity(id);
};
const createContent = async (data) => {
  const {
    _id,
    createDate,
    changeDate,
    isDeleted,
    type,
    hasAffiliate,
    brand,
    deleteDate,
    vgwortPixel,
    scheduledUpdates,
    legacy,
    ...payload
  } = data;
  return postEntity({
    ...payload,
    seoDescription: payload.seoDescription || getDefaultSeoDescription(data)
  });
};
const updateContent = async (contentUuid, data) => {
  const {
    createDate,
    changeDate,
    isDeleted,
    type,
    hasAffiliate,
    brand,
    deleteDate,
    initialPublishedTitle,
    initialPublishDate,
    vgwortPixel,
    scheduledUpdates,
    legacy,
    ...payload
  } = data;
  return putEntity(contentUuid, {
    ...payload,
    seoDescription: payload.seoDescription || getDefaultSeoDescription(data)
  });
};
const deleteContent = (contentUuid) => deleteEntity(contentUuid);
const getTrackingValuesJobs = async () => {
  return (await getClient().get("/cms/tracking-values/bf-jobs")).data;
};
const getContentWorkflowUrl = (contentUuid) => `cms/contents/${contentUuid}/workflow`;
const getContentWorkflow = async (contentUuid) => {
  return (await getClient().get(getContentWorkflowUrl(contentUuid))).data;
};
const createContentWorkflow = async ({ contentUuid, data }) => {
  return await getClient().post(getContentWorkflowUrl(contentUuid), data);
};
const updateContentWorkflow = async ({ contentUuid, data }) => {
  return await getClient().put(getContentWorkflowUrl(contentUuid), data);
};
const partialUpdateContentWorkflow = async ({ contentUuid, data }) => {
  return await getClient().patch(getContentWorkflowUrl(contentUuid), data);
};
const getContentLocks = async (contentUuids) => {
  return (await getClient().get("/cms/locks", { params: { contentUuids } })).data;
};
const getScheduledChange = async (contentUuid) => {
  const client = getClient();
  const response = await client.get(`/cms/contents/${contentUuid}/scheduled-change`);
  return response.data;
};
const createScheduledChange = async (contentUuid, data) => {
  const client = getClient();
  const scheduledChangeData = {
    ...data,
    contentsRef: removeKeysFromContentBlocks(data.contentsRef)
  };
  const response = await client.post(`/cms/contents/${contentUuid}/scheduled-change`, scheduledChangeData);
  return response.data;
};
const updateScheduledChange = async (contentUuid, data) => {
  const client = getClient();
  const scheduledChangeData = {
    ...data,
    contentsRef: removeKeysFromContentBlocks(data.contentsRef)
  };
  const response = await client.put(`/cms/contents/${contentUuid}/scheduled-change`, scheduledChangeData);
  return response.data;
};
const deleteScheduledChange = async (contentUuid) => {
  const client = getClient();
  const response = await client.delete(`/cms/contents/${contentUuid}/scheduled-change`);
  return response.data;
};
const updateLegacyMigration = async (contentUuid, data) => {
  const client = getClient();
  const response = await client.put(`/cms/contents/${contentUuid}/legacy-migration`, data);
  return response.data;
};
const getDefaultStateContentBlock = () => addKeyToBlock({ type: "TEXT", content: "<p></p>" });
const getDefaultStateContent = () => ({
  _id: "",
  type: "CONTENT",
  createDate: /* @__PURE__ */ new Date(),
  changeDate: /* @__PURE__ */ new Date(),
  bfJobUuid: "",
  channelUuid: "",
  secondaryChannelUuids: [],
  kicker: "",
  title: "",
  seoTitle: "",
  leadText: "",
  seoDescription: "",
  displayDate: void 0,
  publishDate: void 0,
  paths: [],
  slug: "",
  linkedTagsUuids: [],
  isTeaserDataShown: false,
  contentsRef: [getDefaultStateContentBlock()],
  mainMediaRef: {
    mediaType: "image",
    imageUuid: ""
  },
  ads: {
    adTags: [],
    isAdsEnabled: true,
    hasAdvertisementMark: false,
    isOutstreamEnabled: true
  },
  hasAffiliate: false,
  articleAuthorshipRef: {
    //TODO should be read from enum inside article data schema, which is not possible at the moment
    type: "news_article",
    authorUuids: []
  },
  brand: "",
  listingImageUuid: "",
  unpublishDate: void 0,
  isNoIndex: false,
  isDeleted: false,
  isHiddenFromNewsticker: false,
  isHiddenFromTeaserList: false,
  state: "draft",
  legacy: {
    source: "vibe",
    isEditableInCTF: true,
    isRenderedByCTF: true,
    hasBeenModifiedInCTF: true
  },
  tracking: {
    pageContentSourceDetail: ""
  },
  feeds: {
    msnAt: "exclude",
    msnCh: "exclude",
    msnDe: "auto",
    yahoo: "auto"
  }
});
function hasContentBlockKey(block) {
  return !!block && "_key" in block;
}
function addKeyToBlock(block) {
  if (!block) {
    const defaultBlock = getDefaultStateContentBlock();
    console.error(`Attempted to add a key to non-existing content block -> fallback to default content block ${defaultBlock.type}`);
    return defaultBlock;
  }
  if (hasContentBlockKey(block)) {
    return block;
  }
  return {
    _key: v4(),
    ...block
  };
}
function removeKeyFromBlock(block) {
  if (!hasContentBlockKey(block)) {
    return block;
  }
  const { _key, ...rest } = block;
  return rest;
}
function mutateAddKeysToContentBlocks(content) {
  var _a;
  if (!((_a = content.contentsRef) == null ? void 0 : _a.length))
    return;
  for (let i = 0; i < content.contentsRef.length; i++) {
    const block = content.contentsRef[i];
    if (!hasContentBlockKey(block)) {
      try {
        content.contentsRef[i] = addKeyToBlock(block);
      } catch (error) {
        console.error(`Error adding key to content block on index ${i}:`, error);
      }
    }
  }
}
function mutateRemoveKeysFromContentBlocks(content) {
  var _a;
  if (!((_a = content.contentsRef) == null ? void 0 : _a.length))
    return;
  for (let i = 0; i < content.contentsRef.length; i++) {
    const block = content.contentsRef[i];
    if (hasContentBlockKey(block)) {
      try {
        content.contentsRef[i] = removeKeyFromBlock(block);
      } catch (error) {
        console.error(`Error removing key from content block on index ${i}:`, error);
      }
    }
  }
}
function addKeysToContentBlocks(contentBlocks) {
  if (!(contentBlocks == null ? void 0 : contentBlocks.length))
    return [];
  return contentBlocks.map(addKeyToBlock);
}
function removeKeysFromContentBlocks(contentBlocks) {
  if (!(contentBlocks == null ? void 0 : contentBlocks.length))
    return [];
  return contentBlocks.map(removeKeyFromBlock);
}
const onContentCreatedEventType = "onContentCreated";
class OnContentCreatedEvent extends CustomEvent {
  constructor(detail) {
    super(onContentCreatedEventType, { detail });
  }
}
const useEditorContent = defineStore("content", {
  state: () => ({
    loading: false,
    content: getDefaultStateContent(),
    invalidTabs: [],
    activePanel: "article",
    prefillSource: void 0,
    meta: {
      updateShownDate: false,
      updatePublishDate: false,
      isEditingScheduledChange: false,
      isBrandFieldDisabled: false,
      titleLength: 0,
      teaserTitleLength: 0
    }
  }),
  actions: {
    async createContent() {
      this.loading = true;
      try {
        const { _id } = await createContent(this.content);
        this.content._id = _id;
        this.loading = false;
        return _id;
      } catch (e) {
        this.loading = false;
      }
      return "";
    },
    async updateContent() {
      if (!this.content._id)
        return;
      this.loading = true;
      try {
        await updateContent(this.content._id, this.content);
      } catch (e) {
      }
      this.loading = false;
    },
    async deleteContent() {
      if (!this.content._id)
        return;
      this.loading = true;
      try {
        await deleteContent(this.content._id);
      } catch (e) {
      }
      this.loading = false;
    },
    updateBlocksArray(blocksArray) {
      this.content.contentsRef = blocksArray;
    },
    updateTitleLength(n) {
      this.meta.titleLength = n;
    },
    updateTeaserTitleLength(n) {
      this.meta.teaserTitleLength = n;
    },
    updateInvalidTabs(value) {
      this.invalidTabs.push(value);
    },
    resetInvalidTabs() {
      this.invalidTabs = [];
    },
    updateActivePanel(value) {
      this.activePanel = value;
    },
    updateBrand(newBrand) {
      if (!!newBrand && newBrand !== this.content.brand)
        this.content.secondaryChannelUuids = [];
      this.content.brand = newBrand;
    }
  }
});
const prefillEditorContent = (source, data, meta) => {
  const storeInstance = useEditorContent();
  storeInstance.prefillSource = source;
  storeInstance.content = { ...storeInstance.content, ...data };
  storeInstance.meta = { ...storeInstance.meta, ...meta };
  updateDefaultValues(storeInstance.content);
};
export {
  ContentModelName,
  ContentService,
  OnContentCreatedEvent,
  addKeyToBlock,
  addKeysToContentBlocks,
  createContent,
  createContentWorkflow,
  createScheduledChange,
  deleteContent,
  deleteScheduledChange,
  getContent,
  getContentLocks,
  getContentWorkflow,
  getContents,
  getContentsFromIds,
  getDefaultStateContent,
  getDefaultStateContentBlock,
  getScheduledChange,
  getTrackingValuesJobs,
  hasContentBlockKey,
  mutateAddKeysToContentBlocks,
  mutateRemoveKeysFromContentBlocks,
  onContentCreatedEventType,
  partialUpdateContentWorkflow,
  prefillEditorContent,
  removeKeyFromBlock,
  removeKeysFromContentBlocks,
  updateContent,
  updateContentWorkflow,
  updateLegacyMigration,
  updateScheduledChange,
  useEditorContent
};
//# sourceMappingURL=index.es.js.map
